import { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router";
import { Box, Typography } from "@mui/material";
import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import { useSnackbar } from "notistack";
import { useMultiTableData } from "../../../../components/tables/hooks/useMultiTableData.jsx";
import { applySortingWithAccessorKeys } from "../../../../components/tables/utils/tableUtils.js";
import { getDatum } from "@accredo/fe-shared/services/formatService.js";

/**
 * Flache Liste aller Objekte eines Kunden über alle seine Verträge hinweg
 * (exakter Server-Filter über vertrag.kunde_id_ov, siehe
 * KundeController::customerObjects() / ObjectRepository::getObjectsByCustomer()).
 * Je Objekt per Accordion aufklappbar: Tabelle der Objektsparten (analog zum
 * Objekte-Detail-Panel in components/tables/BankAccountTable.jsx).
 */
export default function CustomerObjectsTable({ customerId, objectRefNumLabel }) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const tableKey = "customerObjects";
  const tablesConfig = useMemo(
    () => [
      {
        key: tableKey,
        label: "Objekte",
        route: `/data/kundes/${customerId}/objects`,
        pageSize: 10,
        storageKey: "v2_table_customer_objects",
        extraParams: {
          include: ["sparten", "vertrag"],
        },
      },
    ],
    [customerId]
  );

  const { tableStates, updateTableState, fetchData } = useMultiTableData(
    tablesConfig,
    enqueueSnackbar
  );
  const tableState = tableStates[tableKey] || {};
  const rows = tableState.rows || [];

  useEffect(() => {
    fetchData(tableKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const columns = useMemo(
    () => [
      { accessorKey: "bezeichnung", header: "Bezeichnung", size: 200 },
      { accessorKey: "strasse", header: "Straße", size: 150 },
      { accessorKey: "hausnr", header: "Hausnummer", size: 100 },
      { accessorKey: "plz", header: "PLZ", size: 80 },
      { accessorKey: "ort", header: "Ort", size: 150 },
      ...(objectRefNumLabel
        ? [
            {
              accessorKey: "customer_reference_number",
              header: objectRefNumLabel,
              size: 180,
            },
          ]
        : []),
      {
        // accessorKey liest weiterhin das API-Feld "typ" (das intern aus
        // vertrags.vertragsart stammt, siehe ObjectRepository), id ist bewusst
        // auf den eigenständigen Filter-Namen "vertragsart" gesetzt, damit der
        // Filter-Query-Parameter zu AllowedFilter::partial('vertragsart', ...) passt.
        accessorKey: "typ",
        id: "vertragsart",
        header: "Vertragsart",
        size: 150,
      },
      {
        accessorKey: "vertrag.vertragsnummer",
        id: "vertrag.vertragsnummer",
        header: "Vertragsnummer",
        size: 150,
        Cell: ({ row }) => {
          const vertragsnummer = row.original.vertrag?.vertragsnummer;
          if (!vertragsnummer) return "—";
          return (
            <Box
              component="button"
              onClick={(event) => {
                event.stopPropagation();
                navigate(
                  `/admin/kunden/${customerId}/vertraege?vertragsnummer=${encodeURIComponent(vertragsnummer)}`
                );
              }}
              sx={{
                border: "none",
                background: "transparent",
                padding: 0,
                margin: 0,
                color: "primary.main",
                cursor: "pointer",
                font: "inherit",
                textAlign: "left",
                textDecoration: "underline",
              }}
            >
              {vertragsnummer}
            </Box>
          );
        },
      },
    ],
    [objectRefNumLabel, customerId, navigate]
  );

  return (
    <DataTable
      columns={columns}
      data={rows}
      isLoading={tableState.isLoading}
      hasLoaded={tableState.hasLoaded}
      enableRowExpansion
      renderRowExpansionContent={(row) => row._expand}
      enablePagination
      manualPagination
      pageIndex={tableState.pageIndex ?? 0}
      pageSize={tableState.pageSize ?? 10}
      totalRows={tableState.totalRows ?? rows.length}
      onChangePage={(nextOrUpdater) => {
        const base = {
          pageIndex: tableState.pageIndex ?? 0,
          pageSize: tableState.pageSize ?? 10,
        };
        const next =
          typeof nextOrUpdater === "function"
            ? nextOrUpdater(base)
            : nextOrUpdater;
        if (!next) return;
        updateTableState(tableKey, {
          pageIndex: next.pageIndex,
          pageSize: next.pageSize,
        });
      }}
      onChangeSorting={(sorting) =>
        updateTableState(tableKey, {
          sorting: applySortingWithAccessorKeys(sorting, columns),
        })
      }
      onChangeFilters={(filters) => updateTableState(tableKey, { filters })}
      onChangeGlobalFilter={(searchword) =>
        updateTableState(tableKey, { searchword })
      }
      storageKey="v2_table_customer_objects"
      renderDetailPanel={({ row }) => (
        <Box sx={{ p: 2 }}>
          {Array.isArray(row.original.sparten) &&
          row.original.sparten.length > 0 ? (
            <DataTable
              columns={[
                { accessorKey: "sparte", header: "Sparte", size: 150 },
                {
                  accessorKey: "zugang",
                  header: "Zugang",
                  size: 110,
                  Cell: ({ row: sparteRow }) =>
                    getDatum(sparteRow.original.zugang),
                },
                {
                  accessorKey: "abgang",
                  header: "Abgang",
                  size: 110,
                  Cell: ({ row: sparteRow }) =>
                    getDatum(sparteRow.original.abgang),
                },
                { accessorKey: "jnetto", header: "Jahresnetto", size: 130 },
                { accessorKey: "jbrutto", header: "Jahresbrutto", size: 130 },
                { accessorKey: "sb_fix", header: "SB fix", size: 100 },
                { accessorKey: "sb_min", header: "SB min", size: 100 },
                { accessorKey: "sb_max", header: "SB max", size: 100 },
                { accessorKey: "sb_prozent", header: "SB %", size: 90 },
              ]}
              data={row.original.sparten}
              pageSize={99999}
              enableFooter={false}
              enableTopToolbar={false}
              enablePagination={false}
              enableColumnFilters={false}
              enableSorting={true}
              enableBottomToolbar={false}
              enableGlobalFilter={false}
            />
          ) : (
            <Typography variant="body2" sx={{ pl: 2 }}>
              Keine Objektsparten verknüpft
            </Typography>
          )}
        </Box>
      )}
    />
  );
}

CustomerObjectsTable.propTypes = {
  customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  objectRefNumLabel: PropTypes.string,
};
